import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';

export const TOASTR_CONFIG = {
  ROW_LIMIT: 500000,
  STYLE_DELAY: 100,
  WARNING_COLOR: '#EFB46B',
  TOAST_WIDTH: '500px',
  TOAST_MIN_WIDTH: '500px',
  POSITION_CLASS: 'toast-top-center'
};

@Injectable({providedIn: 'root'})
export class ToastrUtilsService {
  constructor(private toastr: ToastrService) {}

  /**
   * Wrapper for toastr.warning with default top-center position
   */
  warning(message: string, title?: string, override?: any): void {
    const options = {
      timeOut: 5000,
      positionClass: TOASTR_CONFIG.POSITION_CLASS,
      ...override
    };
    this.toastr.warning(message, title, options);
    this.applyBackgroundColor(TOASTR_CONFIG.WARNING_COLOR);
  }

  /**
   * Wrapper for toastr.error with default top-center position
   */
  error(message: string, title?: string, override?: any): void {
    const options = {
      timeOut: 5000,
      positionClass: TOASTR_CONFIG.POSITION_CLASS,
      ...override
    };
    this.toastr.error(message, title, options);
  }

  /**
   * Wrapper for toastr.success with default top-center position
   */
  success(message: string, title?: string, override?: any): void {
    const options = {
      timeOut: 5000,
      positionClass: TOASTR_CONFIG.POSITION_CLASS,
      ...override
    };
    this.toastr.success(message, title, options);
  }

  /**
   * Wrapper for toastr.info with default top-center position
   */
  info(message: string, title?: string, override?: any): void {
    const options = {
      timeOut: 5000,
      positionClass: TOASTR_CONFIG.POSITION_CLASS,
      ...override
    };
    this.toastr.info(message, title, options);
  }

  /**
   * Apply background color and width styling to ALL toastr notifications.
   * Styles the entire toast container and all individual toasts within it.
   * Uses multiple timing attempts to ensure styles persist even when toasts stack.
   * Uses setProperty with !important flag to override default ngx-toastr styles.
   */
  applyBackgroundColor(color: string = TOASTR_CONFIG.WARNING_COLOR): void {
    const styleAllToasts = () => {
      try {
        // Get the main toast container
        const containerElement = document.querySelector('.toast-top-center');
        if (containerElement) {
          (containerElement as HTMLElement).style.setProperty('width', TOASTR_CONFIG.TOAST_WIDTH, 'important');
          (containerElement as HTMLElement).style.setProperty('min-width', TOASTR_CONFIG.TOAST_MIN_WIDTH, 'important');
        }

        // Style ALL toasts in the container, not just the last one
        const toastElements = document.querySelectorAll('.toast-top-center .ngx-toastr.ng-trigger');
        if (toastElements && toastElements.length > 0) {
          toastElements.forEach((toast) => {
            const toastElement = toast as HTMLElement;
            // Apply all styling to each toast
            toastElement.style.setProperty('background-color', color, 'important');
            toastElement.style.setProperty('min-width', TOASTR_CONFIG.TOAST_MIN_WIDTH, 'important');
            toastElement.style.setProperty('width', TOASTR_CONFIG.TOAST_WIDTH, 'important');
         });
        }
      } catch (error) {
        console.error('Error applying toastr background color:', error);
      }
    };

    // Apply styles multiple times with aggressive retry strategy
    styleAllToasts();
    setTimeout(styleAllToasts, 10);
    setTimeout(styleAllToasts, 25);
    setTimeout(styleAllToasts, 50);
    setTimeout(styleAllToasts, 100);
    setTimeout(styleAllToasts, 150);
    setTimeout(styleAllToasts, 200);
    setTimeout(styleAllToasts, 300);
    setTimeout(styleAllToasts, 500);

    // Continue styling for up to 5 seconds to catch all stacked toasts
    let retryCount = 0;
    const retryInterval = setInterval(() => {
      styleAllToasts();
      retryCount++;
      if (retryCount > 20) {
        clearInterval(retryInterval);
      }
    }, 250);
  }
}
